import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { useDispatch, useSelector } from "react-redux";
import "./ContactDetails.scss";
import { showNotification } from "../../Actions/componentActions";

const emptyForm = {
  firstName: "",
  lastName: "",
  email: "",
  description: "",
};

export default function ContactUsPage() {
  const dispatch = useDispatch();
  const profileDetail = useSelector((state) => state?.profileReducer?.profileDetail);
  const [contactData, setContactData] = useState(() => ({
    ...emptyForm,
    firstName: profileDetail?.data?.user?.first_name || "",
    lastName: profileDetail?.data?.user?.last_name || "",
    email: profileDetail?.data?.user?.email || "",
  }));
  const [isSubmitting, setIsSubmitting] = useState(false);

  const userRole = profileDetail?.data?.user?.role;
  const apiUrl = global.platformURI;

  useEffect(() => {
    setContactData((prev) => ({
      ...prev,
      firstName: prev.firstName || profileDetail?.data?.user?.first_name || "",
      lastName: prev.lastName || profileDetail?.data?.user?.last_name || "",
      email: prev.email || profileDetail?.data?.user?.email || "",
    }));
  }, [
    profileDetail?.data?.user?.email,
    profileDetail?.data?.user?.first_name,
    profileDetail?.data?.user?.last_name,
  ]);

  const contactNote = useMemo(() => {
    if (userRole) {
      return "Need support with your account, assessment access, or results? Send us a message and our team will help.";
    }

    return "Tell us what you are exploring, and we will help you find the best starting point for your team or program.";
  }, [userRole]);

  const handleChange = (event) => {
    const { name, value } = event.target;

    setContactData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setIsSubmitting(true);

    try {
      const formData = new FormData();
      formData.append("contact[first_name]", contactData.firstName || "");
      formData.append("contact[last_name]", contactData.lastName || "");
      formData.append("contact[email]", contactData.email || "");
      formData.append("contact[description]", contactData.description || "");

      const response = await axios.post(`${apiUrl}/api/v1/contacts`, formData);

      if (response.status === 201) {
        dispatch(
          showNotification(
            true,
            "Thanks for contacting us! We will be in touch with you shortly",
            "success"
          )
        );
        setContactData({
          ...emptyForm,
          firstName: profileDetail?.data?.user?.first_name || "",
          lastName: profileDetail?.data?.user?.last_name || "",
          email: profileDetail?.data?.user?.email || "",
        });
      } else {
        dispatch(showNotification(true, "Something went wrong! Please try again."));
      }
    } catch (error) {
      dispatch(showNotification(true, "Unable to send your message right now. Please try again."));
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="contact-container">
      <div className="contact-content">
        <div style={{ marginBottom: "40px" }}>
          <div className="section-eyebrow">Support · Connect</div>
          <h1 className="page-title">
            Let&apos;s start the <em>right conversation.</em>
          </h1>
          <p className="page-subtitle">{contactNote}</p>
        </div>

      <div className="materials-grid">
        {/* Left column - Form */}
        <div className="materials-main">
          <div className="form-card-title">Contact our team</div>
          <div className="form-card-subtitle">
            We read every message and follow up as quickly as we can.
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="form-row">
              <label className="form-label">
                <span className="form-label-text">First name</span>
                <input
                  required
                  type="text"
                  name="firstName"
                  value={contactData.firstName}
                  onChange={handleChange}
                  placeholder="Enter your first name"
                  className="form-input"
                />
              </label>

              <label className="form-label">
                <span className="form-label-text">Last name</span>
                <input
                  required
                  type="text"
                  name="lastName"
                  value={contactData.lastName}
                  onChange={handleChange}
                  placeholder="Enter your last name"
                  className="form-input"
                />
              </label>
            </div>

            <label className="form-label">
              <span className="form-label-text">Email address</span>
              <input
                required
                type="email"
                name="email"
                value={contactData.email}
                onChange={handleChange}
                placeholder="you@example.com"
                className="form-input"
              />
            </label>

            <label className="form-label">
              <span className="form-label-text">How can we help?</span>
              <textarea
                required
                rows={6}
                name="description"
                value={contactData.description}
                onChange={handleChange}
                placeholder="Tell us a bit about your question, issue, or goal."
                className="form-textarea"
              />
            </label>

            <div className="form-footer">
              <p className="form-footer-note">
                By submitting this form, you agree to be contacted by our team about your request.
              </p>

              <button
                type="submit"
                disabled={isSubmitting}
                className="btn btn-primary"
                style={{ minWidth: "160px" }}
              >
                {isSubmitting ? "Sending..." : "Submit Message"}
              </button>
            </div>
          </form>
        </div>

        {/* Right column - Sidebar Cards */}
        <div className="materials-aside">
          <div className="aside-card">
            <div className="aside-card-eyebrow">⬢ Why reach out</div>
            <div className="aside-card-title">
              We can help with access, adoption, and next steps.
            </div>
            <ul className="aside-list">
              <li>
                <strong>Questions</strong> — about the entrepreneurial leadership assessment and how it works.
              </li>
              <li>
                <strong>Support</strong> — for participants who need help accessing their account or completing a flow.
              </li>
              <li>
                <strong>Discovery</strong> — conversations with programs, institutions, and teams exploring entrepreneurial leadership.
              </li>
            </ul>
          </div>

          <div className="aside-card completion">
            <div className="aside-card-eyebrow">▶ Response Time</div>
            <div className="aside-card-title">A few details helps us respond faster.</div>
            <p className="completion-body">
              Share your name, email, and what you need help with. If this is a technical issue,
              please include any relevant page or access details in your message.
            </p>
          </div>
        </div>
      </div>
      </div>
    </div>
  );
}
